"use client";

import { useEffect, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import { useUI } from "../context/UIContext";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Cta from "./Cta";
import { useTmSignOut } from "@/hooks/useTmSignOut";

interface CheckEmailResponseData {
  errors?: { message: string }[];
  data: {
    me: {
      profile: {
        emailConfirmed: boolean;
        sendEmailConfirmation?: {
          email: string;
        };
      };
    };
  };
}

const JoinFlowInfoModal = () => {
  const { state: uiState, dispatch } = useUI();
  const { data: session } = useSession();
  const [checkIfEmailVerified, setCheckIfEmailVerified] = useState(false);
  const [verifyEmailFlow, setVerifyEmailFlow] = useState<string | null>("");
  const [ygbActive, setYgbActive] = useState<string | null>("");

  const router = useRouter();

  const isOpen =
    uiState.joinFlowInfoModal.isOpen &&
    ((session?.accessToken &&
      !uiState.isEmailVerified &&
      verifyEmailFlow === "true" &&
      uiState.joinFlowInfoModal.modalType === "email") ||
      uiState.joinFlowInfoModal.modalType === "signup");

  const tmToken = session?.accessToken;
  const idTokenHint = session?.idToken;

  useTmSignOut();

  useEffect(() => {
    if (verifyEmailFlow !== window.localStorage.getItem("verify_email_flow")) {
      setVerifyEmailFlow(window.localStorage.getItem("verify_email_flow"));
    }
    if (ygbActive !== window.localStorage.getItem("ygb_active")) {
      setYgbActive(window.localStorage.getItem("ygb_active"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState]);

  useEffect(() => {
    if (uiState.joinFlowInfoModal.isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.touchAction = "none";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
      document.body.style.touchAction = "";
      document.body.style.width = "unset";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: verifyEmailData, refetch: sendVerifyEmailRequest } = useQuery<
    AxiosResponse<CheckEmailResponseData>
  >({
    queryKey: ["verify_email"],
    queryFn: () => {
      return axios.post("/api/verify-email", {
        access_token: tmToken,
      });
    },
    enabled: false,
  });

  useEffect(() => {
    if (verifyEmailData && !verifyEmailData.data?.errors?.length) {
      setCheckIfEmailVerified(true);
    }
  }, [verifyEmailData]);

  const { data: checkEmailData } = useQuery<
    AxiosResponse<CheckEmailResponseData>
  >({
    queryKey: ["check_verified_email"],
    queryFn: () => {
      return axios.get("/api/check-email", {
        params: {
          access_token: tmToken,
        },
      });
    },
    enabled: checkIfEmailVerified,
    refetchInterval: checkIfEmailVerified ? 5000 : false,
  });

  useEffect(() => {
    if (checkEmailData?.data?.data?.me?.profile?.emailConfirmed) {
      setCheckIfEmailVerified(false);
      dispatch({
        type: "confirmEmail",
      });
      dispatch({
        type: "openSignupModal",
      });
    }
  }, [checkEmailData, dispatch]);

  const origin =
    typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[rgba(0,0,0,0.8)] z-[9] flex items-center justify-center text-xl w-full h-full">
      <div className="relative bg-tm-c-gray-950 w-[90%] h-fit p-5 text-center rounded-[10px] lg:max-w-[800px] lg:p-0 2xl:max-w-[1000px]">
        {(uiState.joinFlowInfoModal.modalType === "signup" ||
          (uiState.joinFlowInfoModal.modalType === "email" &&
            !verifyEmailData)) && ( // Use verifyEmailData here
          <CloseIcon
            className="absolute top-2.5 right-2.5 text-tm-c-light cursor-pointer"
            style={{
              fontSize: 26,
            }}
            onClick={() => {
              if (verifyEmailFlow === "true") {
                window.localStorage.setItem("verify_email_flow", "false");
              }
              if (ygbActive === "true") {
                window.localStorage.setItem("ygb_active", "false");
              }
              window.localStorage.setItem("next_signup_uri", "");
              if (
                uiState.joinFlowInfoModal.modalType === "email" &&
                !!tmToken
              ) {
                router.push(
                  `https://login.trackmanrange.com/connect/endsession?id_token_hint=${idTokenHint}&post_logout_redirect_uri=${origin}`
                );
              }
              dispatch({
                type: "toggleJoinFlowInfoModal",
                payload: { isOpen: false },
              });
            }}
          />
        )}
        <div className="w-3/5 mx-auto mt-20 mb-15">
          {uiState.joinFlowInfoModal.modalType === "signup" && (
            <>
              {uiState.joinFlowInfoModal.title && (
                <h2 className="text-[25px] lg:text-[40px]">
                  {uiState.joinFlowInfoModal.title}
                </h2>
              )}
              <p className="text-[15px] lg:text-xl">
                To join the tournament, please log in with your Trackman account
                or sign up for a new one.
              </p>
              <Cta
                className="my-2.5 mx-auto transition-opacity duration-300 active:opacity-40 lg:hover:opacity-60"
                name="Go to login"
                onClick={() => {
                  window.localStorage.setItem("ygb_active", "true");
                  signIn("trackman");
                }}
              />
            </>
          )}
          {uiState.joinFlowInfoModal.modalType === "email" &&
            !verifyEmailData && (
              <>
                <p className="text-[15px] lg:text-xl">
                  To sign up for the tournament, you must first verify your
                  email address.
                </p>
                <Cta
                  className="my-2.5 mx-auto transition-opacity duration-300 active:opacity-40 lg:hover:opacity-60"
                  name="Verify email"
                  onClick={() => sendVerifyEmailRequest()}
                />
              </>
            )}
          {uiState.joinFlowInfoModal.modalType === "email" &&
            verifyEmailData?.status === 200 &&
            !verifyEmailData?.data?.errors?.length && (
              <>
                <p className="text-[15px] lg:text-xl">
                  Verification email sent to
                  {`${
                    verifyEmailData?.data?.data?.me?.profile
                      ?.sendEmailConfirmation?.email
                      ? " " +
                        verifyEmailData.data.data.me.profile
                          .sendEmailConfirmation.email
                      : " your trackman email address"
                  }`}
                  . Check your inbox and follow the instructions.
                </p>
                <p className="text-[15px] lg:text-xl">
                  Once confirmed, you&apos;ll be redirected to the next step. To
                  stop sign up, click the button below.
                </p>
                <Cta
                  className="my-2.5 mx-auto transition-opacity duration-300 active:opacity-40 lg:hover:opacity-60"
                  name="Close"
                  onClick={() => {
                    window.localStorage.setItem("verify_email_flow", "false");
                    window.localStorage.setItem("ygb_active", "false");
                    window.localStorage.setItem("next_signup_uri", "");
                    dispatch({
                      type: "toggleJoinFlowInfoModal",
                      payload: { isOpen: false },
                    });
                    router.push(
                      `https://login.trackmanrange.com/connect/endsession?id_token_hint=${idTokenHint}&post_logout_redirect_uri=${origin}`
                    );
                  }}
                />
              </>
            )}
          {uiState.joinFlowInfoModal.modalType === "email" &&
            verifyEmailData &&
            (verifyEmailData.status !== 200 ||
              !!verifyEmailData.data?.errors?.length) && (
              <>
                <p className="text-[15px] lg:text-xl">
                  Ooops... something went wrong. Please try again
                </p>
                <Cta
                  className="my-2.5 mx-auto transition-opacity duration-300 active:opacity-40 lg:hover:opacity-60"
                  name="Close"
                  onClick={() => {
                    window.localStorage.setItem("verify_email_flow", "false");
                    window.localStorage.setItem("ygb_active", "false");
                    window.localStorage.setItem("next_signup_uri", "");
                    dispatch({
                      type: "toggleJoinFlowInfoModal",
                      payload: { isOpen: false },
                    });
                  }}
                />
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default JoinFlowInfoModal;
